import React, {useEffect, useState} from "react";
import axios from "axios";
import {Key} from "lucide-react";
import * as Constants from "../../utils/Constants";
import {getAuthToken, getClientIdOfUser, showToast} from "../../utils/utility";
import Modal from "../../components/ui/Modal";
import TextField from "../../components/ui/TextField";
import Button from "../../components/ui/Button";

/**
 * Compact password-reset modal (≤3 fields → modal rather than slide-over per design spec).
 */
export default function ResetPasswordSlideOver({user, open, onClose}) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setPassword("");
      setConfirm("");
      setError("");
    }
  }, [open]);

  const submit = async () => {
    if (password.length < 6) return setError("Password must be at least 6 characters.");
    if (password !== confirm) return setError("Passwords don't match.");
    try {
      setSaving(true);
      axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;
      const url = `${Constants.BASE_URL}${Constants.API_V1}/${getClientIdOfUser()}/users/${user.userId}/password`;
      await axios.put(url, {password});
      showToast("Password reset", {type: "success"});
      onClose && onClose();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data || "Reset failed.";
      setError(typeof msg === "string" ? msg : "Reset failed.");
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  return (
    <Modal
      open={open}
      onOpenChange={(o) => !o && onClose && onClose()}
      title="Reset password"
      subtitle={`${user.fullName || user.username} · @${user.username}`}
      size="md"
    >
      <Modal.Body>
        <div className="space-y-4">
          <div className="flex items-center gap-3 rounded-lg bg-warning-50 border border-warning-100 px-4 py-3 text-sm text-warning-700">
            <Key className="h-4 w-4 shrink-0" />
            The user will need to log in with this new password and be prompted to change it again.
          </div>
          <TextField
            label="New password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            autoFocus
          />
          <TextField
            label="Confirm password"
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            error={error}
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="primary" onClick={submit} loading={saving}>Reset password</Button>
      </Modal.Footer>
    </Modal>
  );
}
